import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

// SearchableSelect styled components
const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableSelectInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  color: ${props => props.theme.colors.gray[900]};
  transition: all 0.2s ease;
  cursor: text;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary[100]};
    background-color: white;
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }
`;

const SearchableSelectDropdown = styled.div<{
  $isOpen: boolean;
  $top: number;
  $left: number;
  $width: number;
}>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  width: ${props => props.$width}px;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borders.radius.md};
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 9999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SearchableSelectOption = styled.div<{ $isSelected: boolean; $isHighlighted: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  transition: background-color 0.15s ease;

  ${props => {
    if (props.$isSelected) {
      return `
        background-color: ${props.theme.colors.primary[50]};
        color: ${props.theme.colors.primary[700]};
        font-weight: ${props.theme.fontWeights.medium};
      `;
    } else if (props.$isHighlighted) {
      return `
        background-color: ${props.theme.colors.gray[100]};
      `;
    } else {
      return `
        background-color: white;
        color: ${props.theme.colors.gray[900]};
      `;
    }
  }}

  &:hover {
    background-color: ${props => props.$isSelected
      ? props.theme.colors.primary[100]
      : props.theme.colors.gray[100]};
  }
`;

const SearchableSelectEmpty = styled.div`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[500]};
  text-align: center;
`;

// SearchableSelect component
export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get the display label for the selected value
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = isOpen ? searchTerm : (selectedOption?.label || '');

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  };

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen]);

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleOptionClick(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        inputRef.current?.blur();
        break;
    }
  };

  const dropdownContent = isOpen && (
    <SearchableSelectDropdown
      ref={dropdownRef}
      $isOpen={isOpen}
      $top={dropdownPosition.top}
      $left={dropdownPosition.left}
      $width={dropdownPosition.width}
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => (
          <SearchableSelectOption
            key={option.value}
            $isSelected={option.value === value}
            $isHighlighted={index === highlightedIndex}
            onClick={() => handleOptionClick(option.value)}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            {option.label}
          </SearchableSelectOption>
        ))
      ) : (
        <SearchableSelectEmpty>No results found</SearchableSelectEmpty>
      )}
    </SearchableSelectDropdown>
  );

  return (
    <>
      <SearchableSelectContainer ref={containerRef}>
        <SearchableSelectInput
          ref={inputRef}
          id={id}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
        />
      </SearchableSelectContainer>
      {isOpen && createPortal(dropdownContent, document.body)}
    </>
  );
};
